if (!customElements.get('product-form')) {
  customElements.define(
    'product-form',
    class ProductForm extends HTMLElement {
      constructor() {
        super();

        this.form = this.querySelector('form');

        // Shopify keeps this disabled until the JS product form is initialised.
        this.form.querySelector('[name=id]').disabled = false;

        this.form.addEventListener(
          'submit',
          this.onSubmitHandler.bind(this)
        );

        this.cart =
          document.querySelector('cart-notification') ||
          document.querySelector('cart-drawer');

        this.submitButton = this.querySelector('[type="submit"]');

        if (document.querySelector('cart-drawer')) {
          this.submitButton.setAttribute('aria-haspopup', 'dialog');
        }

        this.hideErrors = this.dataset.hideErrors === 'true';
      }

      async onSubmitHandler(evt) {
        evt.preventDefault();

        if (
          this.submitButton.getAttribute('aria-disabled') === 'true'
        ) {
          return;
        }

        /*
         * DIY SUPPLY — PICKUP LOCATION (informational / non-restrictive)
         *
         * Items can be added from ANY location. The pickup store chosen in the
         * header (window.OggLocation) is now just a preference: if one is set we
         * record it on the cart as best-effort note_attributes for the NetSuite
         * order sync, but it is NOT required and never blocks the add.
         *
         * NOTE: because a cart can mix locations, ogg_fulfilment_location_gid
         * only reflects the LAST store selected — it is no longer a reliable
         * per-order fulfilment location (see assets/ogg-location.js header).
         */
        const loc =
          window.OggLocation && typeof window.OggLocation.get === 'function'
            ? window.OggLocation.get()
            : null;

        this.handleErrorMessage();

        this.submitButton.setAttribute('aria-disabled', true);
        this.submitButton.classList.add('loading');

        const spinner = this.querySelector(
          '.loading-overlay__spinner'
        );

        if (spinner) {
          spinner.classList.remove('hidden');
        }

        const config = fetchConfig('javascript');

        config.headers['X-Requested-With'] = 'XMLHttpRequest';
        delete config.headers['Content-Type'];

        const formData = new FormData(this.form);

        if (this.cart) {
          formData.append(
            'sections',
            this.cart
              .getSectionsToRender()
              .map((section) => section.id)
          );

          formData.append(
            'sections_url',
            window.location.pathname
          );

          this.cart.setActiveElement(document.activeElement);
        }

        /*
         * Existing DIY Supply selling-plan logic.
         */
        if (typeof window.getCurrentSellingPlanId === 'function') {
          const sellingPlanId =
            window.getCurrentSellingPlanId();

          if (sellingPlanId) {
            formData.append(
              'selling_plan',
              sellingPlanId
            );
          }
        }

        config.body = formData;

        try {
          /*
           * STEP 1:
           * Add the actual product to the Shopify cart.
           */
          const cartAddResponse = await fetch(
            routes.cart_add_url,
            config
          );

          const response = await cartAddResponse.json();

          if (response.status) {
            publish(PUB_SUB_EVENTS.cartError, {
              source: 'product-form',
              productVariantId: formData.get('id'),
              errors: response.description,
              message: response.message,
            });

            this.handleErrorMessage(
              response.description
            );

            const soldOutMessage =
              this.submitButton.querySelector(
                '.sold-out-message'
              );

            if (!soldOutMessage) {
              return;
            }

            this.submitButton.setAttribute(
              'aria-disabled',
              true
            );

            const buttonText =
              this.submitButton.querySelector('span');

            if (buttonText) {
              buttonText.classList.add('hidden');
            }

            soldOutMessage.classList.remove('hidden');

            this.error = true;
            return;
          }

          /*
           * STEP 2 (optional):
           * If the customer has chosen a pickup store, stamp it onto the cart as
           * best-effort note_attributes for the NetSuite order sync. This is no
           * longer required — the item has already been added above — so a
           * failure here is logged but does NOT abort or error the add.
           */
          if (loc && loc.gid) {
            try {
              const locationResponse = await fetch(
                `${window.Shopify.routes.root}cart/update.js`,
                {
                  method: 'POST',
                  headers: {
                    'Content-Type': 'application/json',
                    'X-Requested-With': 'XMLHttpRequest',
                  },
                  body: JSON.stringify({
                    attributes: {
                      ogg_fulfilment_location: loc.name,
                      ogg_fulfilment_location_gid: loc.gid,
                    },
                  }),
                }
              );

              if (!locationResponse.ok) {
                console.warn(
                  'DIY Supply: could not record pickup location preference (item was still added).'
                );
              }
            } catch (attrErr) {
              console.warn(
                'DIY Supply: pickup location preference write failed (item was still added):',
                attrErr
              );
            }
          }

          /*
           * If this theme isn't using the cart drawer,
           * go to the normal cart after everything has
           * been saved.
           */
          if (!this.cart) {
            window.location =
              window.routes.cart_url;
            return;
          }

          if (!this.error) {
            publish(PUB_SUB_EVENTS.cartUpdate, {
              source: 'product-form',
              productVariantId: formData.get('id'),
            });
          }

          this.error = false;

          const quickAddModal =
            this.closest('quick-add-modal');

          if (quickAddModal) {
            document.body.addEventListener(
              'modalClosed',
              () => {
                setTimeout(() => {
                  this.cart.renderContents(response);
                });
              },
              { once: true }
            );

            quickAddModal.hide(true);
          } else {
            this.cart.renderContents(response);
          }
        } catch (e) {
          console.error(
            'DIY Supply product form error:',
            e
          );

          this.handleErrorMessage(
            'Something went wrong while adding this item to your cart. Please try again.'
          );
        } finally {
          this.submitButton.classList.remove('loading');

          if (
            this.cart &&
            this.cart.classList.contains('is-empty')
          ) {
            this.cart.classList.remove('is-empty');
          }

          if (!this.error) {
            this.submitButton.removeAttribute(
              'aria-disabled'
            );
          }

          const spinner = this.querySelector(
            '.loading-overlay__spinner'
          );

          if (spinner) {
            spinner.classList.add('hidden');
          }
        }
      }

      handleErrorMessage(errorMessage = false) {
        if (this.hideErrors) {
          return;
        }

        this.errorMessageWrapper =
          this.errorMessageWrapper ||
          this.querySelector(
            '.product-form__error-message-wrapper'
          );

        if (!this.errorMessageWrapper) {
          return;
        }

        this.errorMessage =
          this.errorMessage ||
          this.errorMessageWrapper.querySelector(
            '.product-form__error-message'
          );

        this.errorMessageWrapper.toggleAttribute(
          'hidden',
          !errorMessage
        );

        if (errorMessage) {
          this.errorMessage.textContent =
            errorMessage;
        }
      }
    }
  );
}